import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { WmmBrandStack } from "@/pages/LoginPage";

const PLEX = '"IBM Plex Sans", ui-sans-serif, system-ui, -apple-system, sans-serif';

/**
 * SSO landing route: WMM redirects here with ?token=<one-shot jwt>.
 * We exchange it immediately (60s TTL) for a local session, then drop the
 * token forever. A ref guards against double-execution (React StrictMode
 * double-mounts effects and the token is strictly one-shot).
 */
export default function SsoCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { ssoExchange } = useAuth();
  const ranRef = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = params.get("token");
    if (!token) {
      navigate("/login?error=sso_failed", { replace: true });
      return;
    }
    // Scrub the one-shot token out of the address bar immediately.
    window.history.replaceState({}, "", "/sso");

    ssoExchange(token)
      .then(() => navigate("/", { replace: true }))
      .catch(() => {
        setFailed(true);
        navigate("/login?error=sso_failed", { replace: true });
      });
  }, [params, navigate, ssoExchange]);

  return (
    <div
      data-testid="sso-callback-page"
      className="min-h-screen bg-[#fafafa] flex flex-col items-center justify-center gap-6 px-4"
      style={{ fontFamily: PLEX }}
    >
      <WmmBrandStack />
      <div className="flex items-center gap-2.5 rounded-[10px] border border-black/[0.06] bg-white px-5 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.06)]">
        <Loader2 size={15} className="animate-spin text-[#57534e]" />
        <span data-testid="sso-status-text" className="text-[13px] text-[#1c1917]">
          {failed ? "Sign-in failed - redirecting\u2026" : "Completing sign-in with Web Marketing Machine\u2026"}
        </span>
      </div>
    </div>
  );
}
